import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Divider,
} from "@material-ui/core";
import { Add, Delete } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";

import select from "../../utils/select";
import { getAllProfile } from "../../reducer/profile/action";
import { getAllDepartments } from "../../reducer/department/action";
import { uploadMultiFile } from "../../reducer/file/action";
import { insertTopic, updateTopic } from "../../reducer/researchTopic/action";
import {
  TOPIC_LEVELS,
  TOPIC_ROLES,
  TOPIC_DURATIONS,
  TOPIC_MEMBER_TYPES,
} from "../../constants/topicStatus";
import MultiFileManager from "../File/MultiFileManager/MultiFileManager";

const emptyMember = (type = "internal") => ({
  memberType: type,
  profileId: 0,
  departmentId: 0,
  fullName: "",
  faculty: "",
  identityNumber: "",
  role: "member",
  email: "",
  phone: "",
});

const toDateInput = (v) => (v ? String(v).slice(0, 10) : "");

// Cộng số tháng vào một chuỗi ngày "YYYY-MM-DD" (tính theo local, tránh lệch timezone).
const addMonths = (dateStr, months) => {
  if (!dateStr || !months) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const base = new Date(y, m - 1 + Number(months), d);
  const mm = String(base.getMonth() + 1).padStart(2, "0");
  const dd = String(base.getDate()).padStart(2, "0");
  return `${base.getFullYear()}-${mm}-${dd}`;
};

export default function ResearchTopicFormModal({ open, onClose, topic, reloadPayload }) {
  const dispatch = useDispatch();
  const profileList = useSelector((s) => {
    const v = select(s, "profileReducer", "list");
    return v && v.toJS ? v.toJS() : v || [];
  });
  const departments = useSelector((s) => {
    const v = select(s, "departmentReducer", "departments");
    return v && v.toJS ? v.toJS() : v || [];
  });

  const [values, setValues] = useState({});
  const [members, setMembers] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!profileList.length) dispatch(getAllProfile());
    if (!departments.length) dispatch(getAllDepartments());
  }, [dispatch]); // eslint-disable-line

  useEffect(() => {
    if (open) {
      setValues({
        titleVi: topic?.titleVi || "",
        titleEn: topic?.titleEn || "",
        year: topic?.year || new Date().getFullYear(),
        level: topic?.level || "school",
        field: topic?.field || "",
        duration: topic?.duration || 12,
        numOfMember: topic?.numOfMember || 1,
        startAt: toDateInput(topic?.startAt),
        finishAt: toDateInput(topic?.finishAt),
        estimatedCost: topic?.estimatedCost || 0,
        hostDepartmentId: topic?.hostDepartmentId || 0,
        sponsor: topic?.sponsor || "",
        expectedResult: topic?.expectedResult || "",
        proposalFileUrl: topic?.proposalFileUrl || "",
      });
      setMembers(topic?.members?.length ? topic.members : [emptyMember("internal")]);
      setNewFiles([]);
      setErrors({});
    }
  }, [open, topic]);

  // Ngày kết thúc = ngày bắt đầu + thời lượng — tự tính, người dùng không sửa tay.
  useEffect(() => {
    const computed = addMonths(values.startAt, values.duration);
    if (computed && computed !== values.finishAt) {
      setValues((prev) => ({ ...prev, finishAt: computed }));
    }
  }, [values.startAt, values.duration]); // eslint-disable-line

  const change = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  // Kiểm tra các trường bắt buộc (đánh dấu * trên nhãn). Trả về map lỗi.
  const validate = () => {
    const e = {};
    if (!String(values.titleVi || "").trim()) e.titleVi = "Vui lòng nhập tên đề tài";
    if (!values.year) e.year = "Vui lòng nhập năm học";
    if (!values.level) e.level = "Vui lòng chọn cấp";
    if (!values.duration) e.duration = "Vui lòng chọn thời lượng";
    if (!values.startAt) e.startAt = "Vui lòng chọn ngày bắt đầu";
    // finishAt là dẫn xuất (bắt đầu + thời lượng) → không validate riêng.
    if (!values.hostDepartmentId) e.hostDepartmentId = "Vui lòng chọn đơn vị chủ trì";
    // Mỗi dòng thành viên đã thêm bắt buộc phải chọn/nhập người.
    const memberErrs = members.map((m) =>
      m.memberType === "internal"
        ? (m.profileId ? "" : "Vui lòng chọn giảng viên")
        : (String(m.fullName || "").trim() ? "" : "Vui lòng nhập họ và tên")
    );
    if (memberErrs.some(Boolean)) e.members = memberErrs;
    return e;
  };

  const changeMember = (idx, patch) => {
    setMembers((prev) => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
    // Xoá lỗi của dòng thành viên khi người dùng sửa lại.
    setErrors((prev) => {
      if (!prev.members || !prev.members[idx]) return prev;
      const next = prev.members.slice();
      next[idx] = "";
      return { ...prev, members: next.some(Boolean) ? next : undefined };
    });
  };

  const addMember = () => setMembers((prev) => [...prev, emptyMember("internal")]);
  const removeMember = (idx) => setMembers((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSaving(true);
    const payload = {
      ...values,
      year: Number(values.year) || 0,
      duration: Number(values.duration) || 0,
      numOfMember: Number(values.numOfMember) || 0,
      estimatedCost: Number(values.estimatedCost) || 0,
      hostDepartmentId: Number(values.hostDepartmentId) || 0,
      startAt: values.startAt ? new Date(values.startAt).toISOString() : null,
      finishAt: values.finishAt ? new Date(values.finishAt).toISOString() : null,
      members: members.map((m) => ({
        ...m,
        profileId: Number(m.profileId) || 0,
        departmentId: Number(m.departmentId) || 0,
      })),
    };
    let res;
    if (topic?.id) res = await dispatch(updateTopic(topic.id, payload, reloadPayload));
    else res = await dispatch(insertTopic(payload, reloadPayload));
    if (res?.success && res.data?.id && newFiles.length) {
      const up = await dispatch(uploadMultiFile(res.data.id, newFiles, "researchTopic"));
      // Gắn URL file thuyết minh vào đề tài để lúc Nộp kiểm tra được (bắt buộc có file).
      if (up?.success && up.data && up.data.length) {
        await dispatch(updateTopic(res.data.id, { ...payload, proposalFileUrl: up.data[0] }, reloadPayload));
      }
    }
    setSaving(false);
    if (res?.success) onClose(true);
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="lg">
      <DialogTitle>{topic?.id ? "Chỉnh sửa đề tài" : "Thêm đề tài mới"}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Cột thông tin */}
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle2" gutterBottom>Thông tin cơ bản</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label="Tên đề tài (Tiếng Việt) *" fullWidth value={values.titleVi || ""} onChange={(e) => change("titleVi", e.target.value)} error={!!errors.titleVi} helperText={errors.titleVi} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Tên đề tài (Tiếng Anh)" fullWidth value={values.titleEn || ""} onChange={(e) => change("titleEn", e.target.value)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Năm học *" type="number" fullWidth value={values.year || ""} onChange={(e) => change("year", e.target.value)} error={!!errors.year} helperText={errors.year} />
              </Grid>
              <Grid item xs={6} md={5}>
                <TextField select label="Cấp *" fullWidth value={values.level || ""} onChange={(e) => change("level", e.target.value)} error={!!errors.level} helperText={errors.level}>
                  {TOPIC_LEVELS.map((l) => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Lĩnh vực" fullWidth value={values.field || ""} onChange={(e) => change("field", e.target.value)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField select label="Thời lượng *" fullWidth value={values.duration || ""} onChange={(e) => change("duration", e.target.value)} error={!!errors.duration} helperText={errors.duration}>
                  {TOPIC_DURATIONS.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Số lượng thành viên" type="number" fullWidth value={values.numOfMember || ""} onChange={(e) => change("numOfMember", e.target.value)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Ngày bắt đầu *" type="date" fullWidth InputLabelProps={{ shrink: true }} value={values.startAt || ""} onChange={(e) => change("startAt", e.target.value)} error={!!errors.startAt} helperText={errors.startAt} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Ngày kết thúc" type="date" fullWidth disabled InputLabelProps={{ shrink: true }} value={values.finishAt || ""} helperText="Tự tính theo ngày bắt đầu + thời lượng" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Chi phí dự kiến (VNĐ)" type="number" fullWidth value={values.estimatedCost || ""} onChange={(e) => change("estimatedCost", e.target.value)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField select label="Đơn vị chủ trì *" fullWidth value={values.hostDepartmentId || 0} onChange={(e) => change("hostDepartmentId", e.target.value)} error={!!errors.hostDepartmentId} helperText={errors.hostDepartmentId}>
                  <MenuItem value={0}>-- Chọn --</MenuItem>
                  {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Đơn vị tài trợ" fullWidth value={values.sponsor || ""} onChange={(e) => change("sponsor", e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Kết quả dự kiến" fullWidth multiline rows={2} value={values.expectedResult || ""} onChange={(e) => change("expectedResult", e.target.value)} />
              </Grid>
            </Grid>

            <Divider style={{ margin: "16px 0" }} />
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Thành viên</Typography>
              <Button size="small" startIcon={<Add />} onClick={addMember}>Thêm thành viên</Button>
            </Grid>
            {members.map((m, idx) => (
              <Grid container spacing={1} key={idx} alignItems="center" style={{ marginTop: 4 }}>
                <Grid item xs={6} md={2}>
                  <TextField select label="Loại" fullWidth value={m.memberType} onChange={(e) => changeMember(idx, { memberType: e.target.value })}>
                    {TOPIC_MEMBER_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </TextField>
                </Grid>
                {m.memberType === "internal" ? (
                  <Grid item xs={6} md={4}>
                    <Autocomplete
                      options={profileList}
                      getOptionLabel={(o) => o.name || ""}
                      value={profileList.find((p) => p.id === m.profileId) || null}
                      onChange={(_, val) => changeMember(idx, { profileId: val?.id || 0, fullName: val?.name || "", email: val?.email || "", departmentId: val?.departments?.[0]?.departmentId || 0 })}
                      renderInput={(params) => <TextField {...params} label="Chọn giảng viên *" error={!!errors.members?.[idx]} helperText={errors.members?.[idx]} />}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={6} md={4}>
                    <TextField label="Họ và tên *" fullWidth value={m.fullName} onChange={(e) => changeMember(idx, { fullName: e.target.value })} error={!!errors.members?.[idx]} helperText={errors.members?.[idx]} />
                  </Grid>
                )}
                {m.memberType === "student" && (
                  <>
                    <Grid item xs={6} md={2}>
                      <TextField label="Khoa" fullWidth value={m.faculty} onChange={(e) => changeMember(idx, { faculty: e.target.value })} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField label="CCCD" fullWidth value={m.identityNumber} onChange={(e) => changeMember(idx, { identityNumber: e.target.value })} />
                    </Grid>
                  </>
                )}
                <Grid item xs={6} md={2}>
                  <TextField select label="Vai trò" fullWidth value={m.role} onChange={(e) => changeMember(idx, { role: e.target.value })}>
                    {TOPIC_ROLES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={5} md={1}>
                  <TextField label="Email" fullWidth value={m.email} onChange={(e) => changeMember(idx, { email: e.target.value })} />
                </Grid>
                <Grid item xs={1}>
                  <IconButton size="small" onClick={() => removeMember(idx)}><Delete fontSize="small" /></IconButton>
                </Grid>
              </Grid>
            ))}
          </Grid>

          {/* Cột file minh chứng */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>File thuyết minh đề tài</Typography>
            <MultiFileManager
              fileUrls={values.proposalFileUrl ? [values.proposalFileUrl] : []}
              setFileUrls={(urls) => change("proposalFileUrl", urls[0] || "")}
              newFiles={newFiles}
              setNewFiles={setNewFiles}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Huỷ</Button>
        <Button color="primary" variant="contained" disabled={saving} onClick={handleSubmit}>
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  );
}
